// File: frontend/src/components/seo-analytics/AhrefsAlertBanner.jsx
// Version: 1.1
// Date: 2026-04-27
// Description: Color-coded alert banner for active Ahrefs traffic-decline alerts.
//              Renders nothing if alerts list is empty. Severity colors:
//                - 'warning' (10-25% drop) → yellow
//                - 'critical' (>25% drop) → red
//              Includes per-alert "Dismiss" button (session-only, not persisted).
// History:
// - [v1.1]: Light-theme polish — reverified yellow/red banners read
//           cleanly on the new #f8fafc page background. No structural
//           change.
// - [v1.0]: Initial implementation.

import React, { useEffect, useState } from 'react';
import seoAnalyticsService from '../../services/seoAnalyticsService';

const formatNum = (n) => (n == null ? '—' : Number(n).toLocaleString());
const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const AhrefsAlertBanner = ({ domainId }) => {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!domainId) return;
    let cancelled = false;
    const fetchAlerts = async () => {
      setLoading(true);
      try {
        const data = await seoAnalyticsService.getAhrefsAlerts(domainId);
        if (!cancelled) setAlerts(Array.isArray(data) ? data : []);
      } catch (err) {
        // Soft-fail: alerts are advisory; don't toast — just log.
        // Banner simply won't appear.
        console.warn('[AhrefsAlertBanner] Failed to fetch alerts:', err.message);
        if (!cancelled) setAlerts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAlerts();
    return () => {
      cancelled = true;
    };
  }, [domainId]);

  const dismiss = (id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  if (loading || !alerts.length) return null;

  const visible = alerts.filter((a) => {
    const id = a.notification_id || `${a.domain_id}-${a.created_at}`;
    return !dismissed.has(id);
  });

  if (!visible.length) return null;

  return (
    <div className="ahrefs-alert-stack">
      {visible.map((a, idx) => {
        const id = a.notification_id || `${a.domain_id}-${a.created_at}`;
        const severity = a.severity === 'critical' ? 'critical' : 'warning';
        const arrow = '↓';
        const pct = a.drop_pct != null ? Number(a.drop_pct).toFixed(1) : '—';
        return (
          <div
            key={id || idx}
            className={`ahrefs-alert-banner ahrefs-alert-${severity}`}
            role="alert"
          >
            <div className="ahrefs-alert-content">
              <span className="ahrefs-alert-icon" aria-hidden="true">
                {severity === 'critical' ? '⚠' : '⚠'}
              </span>
              <div className="ahrefs-alert-body">
                <strong className="ahrefs-alert-title">
                  Traffic dropped {pct}% {arrow} week-over-week
                </strong>
                <span className="ahrefs-alert-detail">
                  {formatNum(a.week_ago_value)} → {formatNum(a.current_value)} · Last
                  collected: {formatDate(a.created_at)}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="ahrefs-alert-dismiss"
              onClick={() => dismiss(id)}
              aria-label="Dismiss alert"
            >
              Dismiss
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default AhrefsAlertBanner;
